import React from 'react';

export type TermsSection = {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

const TERMS_SECTIONS: TermsSection[] = [
  {
    id: 'overview',
    heading: '1. Overview of the Service',
    paragraphs: [
      'LSI Mortality Calculator is an informational tool that allows users to input personal data and selected health-related conditions to generate general longevity-risk categories (e.g., "low," "moderate," or "high").',
      'The Service is intended solely for informational, educational, and personal use.',
      'The Service is not a medical device, and the information it provides is not a diagnosis, not clinical guidance, and not a substitute for professional medical advice.',
    ],
  },
  {
    id: 'no-advice',
    heading: '2. No Medical, Legal, Financial, or Actuarial Advice',
    paragraphs: ['The Service does not provide:'],
    bullets: [
      'medical advice',
      'diagnosis or treatment',
      'legal advice',
      'actuarial analysis or underwriting decisions',
      'professional risk assessments',
      'medical or insurance guidance',
    ],
  },
  {
    id: 'eligibility',
    heading: '3. Eligibility',
    paragraphs: [
      'You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Service. By using the Service, you represent that you meet this requirement.',
    ],
  },
  {
    id: 'user-accounts',
    heading: '4. User Accounts',
    paragraphs: [
      'To access the Service, you may be required to create an account and provide an email address for authentication. You agree to:',
    ],
    bullets: [
      'provide accurate information',
      'maintain the security of your login credentials',
      'notify us promptly of any unauthorized use',
    ],
  },
  {
    id: 'privacy',
    heading: '5. Privacy and Data',
    paragraphs: [
      'By using the Service, you agree to our Privacy Policy, which describes how we collect, use, and store your data.',
      'Important: Although users may enter information about personal conditions, LSI Mortality Calculator is not a HIPAA-covered entity and does not provide healthcare services or process data on behalf of healthcare providers or insurers. Therefore, information you enter into the Service is not considered Protected Health Information (PHI) under HIPAA.',
      'We treat your data with care and use industry-standard security practices, but no system is completely secure. You use the Service at your own risk.',
    ],
  },
  {
    id: 'responsibilities',
    heading: '6. User Responsibilities',
    paragraphs: ['You agree not to:'],
    bullets: [
      'misuse the Service or interfere with its operation',
      'use the Service for commercial, clinical, or insurance-related purposes',
      'attempt to reverse engineer or copy the underlying software',
      'input information belonging to another person without permission',
      'use the Service in any way that violates applicable laws',
    ],
  },
  {
    id: 'limitations',
    heading: '7. Limitations and Disclaimers',
    paragraphs: ['You acknowledge and agree that:'],
    bullets: [
      'the Service provides generalized, non-personalized information',
      'outputs are based on simplified models and may not be accurate',
      'life expectancy estimates and risk levels cannot predict individual outcomes',
      'we are not responsible for decisions you make based on the information provided',
    ],
  },
  {
    id: 'liability',
    heading: '8. Limitation of Liability',
    paragraphs: [
      'To the fullest extent permitted by law, LSI Mortality Calculator and its owners, developers, and affiliates will not be liable for:',
    ],
    bullets: [
      'any indirect, incidental, consequential, or punitive damages',
      'loss of data, profits, or goodwill',
      'reliance on information generated by the Service',
      'any damages resulting from unauthorized access to your data',
    ],
  },
  {
    id: 'modifications',
    heading: '9. Modifications to the Service',
    paragraphs: [
      'We may update, modify, or discontinue portions of the Service at any time without notice. We are not liable to you or any third party for such changes.',
    ],
  },
  {
    id: 'termination',
    heading: '10. Termination',
    paragraphs: [
      'We may suspend or terminate your access to the Service if you violate these Terms or if we believe your use poses a risk to the Service or other users.',
      'You may delete your account at any time.',
    ],
  },
  {
    id: 'governing-law',
    heading: '11. Governing Law',
    paragraphs: [
      'These Terms are governed by the laws of the State of Washington, without regard to conflict of law principles. You agree to submit to the exclusive jurisdiction of courts located in Washington for any disputes arising from these Terms.',
    ],
  },
];

export function TermsContent() {
  return (
    <section className="panel">
      <h1 className="section-title">Terms of Use</h1>
      <p className="muted">Last updated: 11/22/25</p>
      <p className="body-text">
        These Terms of Use ("Terms") govern your access to and use of the LSI Mortality Calculator mobile application, website, and related services (collectively, the "Service").
      </p>
      <p className="body-text">
        By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
      </p>

      {TERMS_SECTIONS.map(section => (
        <article key={section.id} className="terms-section">
          <h2>{section.heading}</h2>
          {section.paragraphs.map((paragraph, idx) => (
            <p key={`${section.id}-paragraph-${idx}`} className="body-text">
              {paragraph}
            </p>
          ))}
          {section.bullets ? (
            <ul>
              {section.bullets.map((bullet, bulletIdx) => (
                <li key={`${section.id}-bullet-${bulletIdx}`}>{bullet}</li>
              ))}
            </ul>
          ) : null}
        </article>
      ))}
    </section>
  );
}
